import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import {
  FloppyDisk,
  CheckCircle,
  CircleNotch,
  Warning,
  Plus,
  Trash,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react";
import type { FooterConfig, SocialPlatform } from "@/types/footer";
import { SAFE_HREF_PATTERN } from "@/schemas/footer";

/** Sentinel vrijednosti za Select: link koji nije stranica, i link koji pokazuje
 * na slug bez objavljene stranice. Ne mogu se sudariti sa href-om (počinju "__"). */
const CUSTOM_URL = "__custom__";
const MISSING_PAGE = "__missing__";

type PageRow = {
  id: string;
  slug: string;
  locale: string;
  title: string;
  status: string;
  groupId: string;
};

type PageOption = {
  /** href koji ide u footer, uvijek /pages/<slug> */
  href: string;
  titleBs: string;
  titleEn: string;
};

/** Jedna stavka po grupi prijevoda: bs stranica nosi slug, en samo naslov. */
function buildPageOptions(rows: PageRow[]): PageOption[] {
  const groups = new Map<string, { bs?: PageRow; en?: PageRow; first: PageRow }>();
  for (const row of rows) {
    const group = groups.get(row.groupId) ?? { first: row };
    if (row.locale === "bs") group.bs = row;
    if (row.locale === "en") group.en = row;
    groups.set(row.groupId, group);
  }
  return [...groups.values()]
    .map(({ bs, en, first }) => {
      const primary = bs ?? en ?? first;
      return {
        href: `/pages/${primary.slug}`,
        titleBs: bs?.title ?? primary.title,
        titleEn: en?.title ?? "",
      };
    })
    .sort((a, b) => a.titleBs.localeCompare(b.titleBs, "bs"));
}

const SOCIAL_PLATFORMS: { platform: SocialPlatform; label: string; placeholder: string }[] = [
  { platform: "instagram", label: "Instagram", placeholder: "https://instagram.com/..." },
  { platform: "facebook", label: "Facebook", placeholder: "https://facebook.com/..." },
  { platform: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/company/..." },
  { platform: "x", label: "X (Twitter)", placeholder: "https://x.com/..." },
  { platform: "youtube", label: "YouTube", placeholder: "https://youtube.com/@..." },
  { platform: "tiktok", label: "TikTok", placeholder: "https://tiktok.com/@..." },
];

/** Odredište jednog footer linka: objavljena stranica ili slobodno upisan URL. */
function LinkTargetField({
  href,
  options,
  knownHrefs,
  onSelectPage,
  onCustomHref,
}: {
  href: string;
  options: PageOption[];
  knownHrefs: Set<string>;
  onSelectPage: (value: string) => void;
  onCustomHref: (value: string) => void;
}) {
  const isPageHref = href.startsWith("/pages/");
  const isKnownPage = knownHrefs.has(href);
  const missingPage = isPageHref && !isKnownPage;
  const value = isKnownPage ? href : missingPage ? MISSING_PAGE : CUSTOM_URL;

  return (
    <div className="space-y-1.5">
      <Select value={value} onValueChange={onSelectPage}>
        <SelectTrigger className="w-full text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {missingPage && (
            <SelectItem value={MISSING_PAGE}>
              {href} (stranica ne postoji)
            </SelectItem>
          )}
          {options.map((option) => (
            <SelectItem key={option.href} value={option.href}>
              {option.titleBs}
              <span className="ml-2 font-mono text-xs text-muted-foreground">
                {option.href}
              </span>
            </SelectItem>
          ))}
          <SelectItem value={CUSTOM_URL}>Vlastiti URL...</SelectItem>
        </SelectContent>
      </Select>

      {!isPageHref && (
        <>
          <Input
            value={href}
            onChange={(e) => onCustomHref(e.target.value)}
            placeholder="URL (npr. /therapists ili #usluge)"
            className="text-sm font-mono"
          />
          {!SAFE_HREF_PATTERN.test(href) && (
            <p className="flex items-center gap-1.5 text-xs text-amber-600">
              <Warning className="size-3.5 shrink-0" />
              URL mora počinjati sa /, #, http://, https:// ili mailto:
            </p>
          )}
        </>
      )}

      {missingPage && (
        <p className="flex items-center gap-1.5 text-xs text-amber-600">
          <Warning className="size-3.5 shrink-0" />
          Nema objavljene stranice sa ovim slugom, link vodi na "stranica nije
          pronađena".
        </p>
      )}
    </div>
  );
}

export function FooterSettingsSection() {
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<FooterConfig | null>(null);
  const [saved, setSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "footer-config"],
    queryFn: async () => {
      const res = await fetch("/api/footer");
      if (!res.ok) throw new Error("Failed to fetch footer config");
      return res.json() as Promise<FooterConfig>;
    },
  });

  // Objavljene stranice za picker linkova. Bez ovoga se href upisuje napamet,
  // pa preimenovanje slug-a tiho razbije footer link.
  const { data: pagesData } = useQuery({
    queryKey: ["admin", "footer-page-options"],
    queryFn: async () => {
      const res = await fetch("/api/pages?status=published&limit=50");
      if (!res.ok) throw new Error("Failed to fetch pages");
      return res.json() as Promise<{ data: PageRow[]; total: number }>;
    },
    staleTime: 60 * 1000,
  });

  const pageOptions = useMemo(
    () => buildPageOptions(pagesData?.data ?? []),
    [pagesData]
  );
  const pageHrefs = useMemo(
    () => new Set(pageOptions.map((p) => p.href)),
    [pageOptions]
  );
  const pagesTruncated = (pagesData?.total ?? 0) > (pagesData?.data.length ?? 0);

  useEffect(() => {
    if (data) {
      setConfig(structuredClone({ ...data, socials: data.socials ?? [] }));
      setHasChanges(false);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (data: FooterConfig) => {
      const res = await fetch("/api/footer", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      await throwIfNotOk(res, "Greška pri spremanju footer konfiguracije");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "footer-config"] });
      queryClient.invalidateQueries({ queryKey: ["footer-config"] });
      setSaved(true);
      setHasChanges(false);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const markChanged = () => {
    setHasChanges(true);
    setSaved(false);
  };

  const updateDescription = (locale: "bs" | "en", value: string) => {
    if (!config) return;
    setConfig({ ...config, description: { ...config.description, [locale]: value } });
    markChanged();
  };

  const setSocialUrl = (platform: SocialPlatform, url: string) => {
    if (!config) return;
    const socials = (config.socials ?? []).filter((s) => s.platform !== platform);
    if (url.trim()) socials.push({ platform, url: url.trim() });
    // Keep a stable order matching SOCIAL_PLATFORMS.
    socials.sort(
      (a, b) =>
        SOCIAL_PLATFORMS.findIndex((p) => p.platform === a.platform) -
        SOCIAL_PLATFORMS.findIndex((p) => p.platform === b.platform)
    );
    setConfig({ ...config, socials });
    markChanged();
  };

  const updateCopyright = (locale: "bs" | "en", value: string) => {
    if (!config) return;
    setConfig({ ...config, copyright: { ...config.copyright, [locale]: value } });
    markChanged();
  };

  const updateColumnTitle = (colIdx: number, locale: "bs" | "en", value: string) => {
    if (!config) return;
    const columns = [...config.columns];
    columns[colIdx] = {
      ...columns[colIdx],
      title: { ...columns[colIdx].title, [locale]: value },
    };
    setConfig({ ...config, columns });
    markChanged();
  };

  const updateLink = (colIdx: number, linkIdx: number, field: "href" | "label.bs" | "label.en", value: string) => {
    if (!config) return;
    const columns = structuredClone(config.columns);
    const link = columns[colIdx].links[linkIdx];
    if (field === "href") link.href = value;
    else if (field === "label.bs") link.label.bs = value;
    else if (field === "label.en") link.label.en = value;
    setConfig({ ...config, columns });
    markChanged();
  };

  /** Odabir stranice iz pickera: postavi href i popuni prazne labele naslovom stranice. */
  const selectPage = (colIdx: number, linkIdx: number, value: string) => {
    if (!config) return;
    const columns = structuredClone(config.columns);
    const link = columns[colIdx]?.links[linkIdx];
    if (!link) return;
    if (value === CUSTOM_URL) {
      // "#" je najbliži validan prazan href (schema ne prima prazan string).
      link.href = "#";
    } else {
      link.href = value;
      const option = pageOptions.find((p) => p.href === value);
      if (option) {
        if (!link.label.bs.trim()) link.label.bs = option.titleBs;
        if (!link.label.en.trim()) link.label.en = option.titleEn || option.titleBs;
      }
    }
    setConfig({ ...config, columns });
    markChanged();
  };

  const addLink = (colIdx: number) => {
    if (!config) return;
    const columns = structuredClone(config.columns);
    columns[colIdx].links.push({ label: { bs: "", en: "" }, href: "#" });
    setConfig({ ...config, columns });
    markChanged();
  };

  const removeLink = (colIdx: number, linkIdx: number) => {
    if (!config) return;
    const columns = structuredClone(config.columns);
    columns[colIdx].links.splice(linkIdx, 1);
    setConfig({ ...config, columns });
    markChanged();
  };

  const moveLink = (colIdx: number, linkIdx: number, direction: "up" | "down") => {
    if (!config) return;
    const columns = structuredClone(config.columns);
    const links = columns[colIdx].links;
    const newIdx = direction === "up" ? linkIdx - 1 : linkIdx + 1;
    if (newIdx < 0 || newIdx >= links.length) return;
    [links[linkIdx], links[newIdx]] = [links[newIdx], links[linkIdx]];
    setConfig({ ...config, columns });
    markChanged();
  };

  const updateMerchantTitle = (locale: "bs" | "en", value: string) => {
    if (!config) return;
    const merchant = config.merchant ?? { title: { bs: "", en: "" }, lines: [] };
    setConfig({ ...config, merchant: { ...merchant, title: { ...merchant.title, [locale]: value } } });
    markChanged();
  };

  const updateMerchantLine = (lineIdx: number, locale: "bs" | "en", value: string) => {
    if (!config?.merchant) return;
    const lines = structuredClone(config.merchant.lines);
    const line = lines[lineIdx];
    if (!line) return;
    line[locale] = value;
    setConfig({ ...config, merchant: { ...config.merchant, lines } });
    markChanged();
  };

  const addMerchantLine = () => {
    if (!config) return;
    const merchant = config.merchant ?? { title: { bs: "", en: "" }, lines: [] };
    setConfig({ ...config, merchant: { ...merchant, lines: [...merchant.lines, { bs: "", en: "" }] } });
    markChanged();
  };

  const removeMerchantLine = (lineIdx: number) => {
    if (!config?.merchant) return;
    const lines = structuredClone(config.merchant.lines);
    lines.splice(lineIdx, 1);
    setConfig({ ...config, merchant: { ...config.merchant, lines } });
    markChanged();
  };

  // Spriječi 400 sa servera: schema odbija href koji ne počinje sa /, #, http(s) ili mailto.
  const hasInvalidHref = (config?.columns ?? []).some((col) =>
    col.links.some((link) => !SAFE_HREF_PATTERN.test(link.href))
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (config && !hasInvalidHref) saveMutation.mutate(config);
  };

  if (isLoading || !config) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="rounded-xl border bg-card p-5">
            <div className="space-y-3">
              <div className="h-5 w-40 animate-pulse rounded bg-muted" />
              <div className="h-10 w-full animate-pulse rounded-lg bg-muted/40" />
              <div className="h-10 w-full animate-pulse rounded-lg bg-muted/40" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Description */}
      <div className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
        <h3
          className="text-base font-semibold text-foreground"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          Opis platforme
        </h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Tekst koji se prikazuje pored logotipa u footeru.
        </p>
        <div className="mt-3 space-y-2">
          <div>
            <Label className="text-xs text-muted-foreground">Bosanski</Label>
            <Input
              value={config.description.bs}
              onChange={(e) => updateDescription("bs", e.target.value)}
              placeholder="Opis na bosanskom..."
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">English</Label>
            <Input
              value={config.description.en}
              onChange={(e) => updateDescription("en", e.target.value)}
              placeholder="Description in English..."
            />
          </div>
        </div>
      </div>

      {/* Columns */}
      {config.columns.map((col, colIdx) => (
        <div key={colIdx} className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
          <h3
            className="text-base font-semibold text-foreground"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Kolona {colIdx + 1}
          </h3>

          {/* Column title */}
          <div className="mt-3 space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Naslov kolone
            </Label>
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <Label className="text-xs text-muted-foreground">BS</Label>
                <Input
                  value={col.title.bs}
                  onChange={(e) => updateColumnTitle(colIdx, "bs", e.target.value)}
                  placeholder="Naslov (BS)"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">EN</Label>
                <Input
                  value={col.title.en}
                  onChange={(e) => updateColumnTitle(colIdx, "en", e.target.value)}
                  placeholder="Title (EN)"
                />
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="mt-4 space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Linkovi
            </Label>
            {pagesTruncated && (
              <p className="text-xs text-muted-foreground">
                Picker prikazuje prvih 50 objavljenih stranica, ostale upišite
                kao vlastiti URL.
              </p>
            )}

            {col.links.map((link, linkIdx) => (
              <div
                key={linkIdx}
                className="rounded-lg border border-border/50 bg-muted/20 p-3"
              >
                <div className="flex items-start gap-2">
                  <div className="flex shrink-0 flex-col gap-0.5 pt-1">
                    <button
                      type="button"
                      onClick={() => moveLink(colIdx, linkIdx, "up")}
                      disabled={linkIdx === 0}
                      className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
                    >
                      <CaretUp className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveLink(colIdx, linkIdx, "down")}
                      disabled={linkIdx === col.links.length - 1}
                      className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
                    >
                      <CaretDown className="size-3.5" />
                    </button>
                  </div>

                  <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="grid gap-1.5 sm:grid-cols-2">
                      <Input
                        value={link.label.bs}
                        onChange={(e) => updateLink(colIdx, linkIdx, "label.bs", e.target.value)}
                        placeholder="Label (BS)"
                        className="text-sm"
                      />
                      <Input
                        value={link.label.en}
                        onChange={(e) => updateLink(colIdx, linkIdx, "label.en", e.target.value)}
                        placeholder="Label (EN)"
                        className="text-sm"
                      />
                    </div>
                    <LinkTargetField
                      href={link.href}
                      options={pageOptions}
                      knownHrefs={pageHrefs}
                      onSelectPage={(value) => selectPage(colIdx, linkIdx, value)}
                      onCustomHref={(value) => updateLink(colIdx, linkIdx, "href", value)}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => removeLink(colIdx, linkIdx)}
                    className="shrink-0 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-rose-50 hover:text-rose-600"
                  >
                    <Trash className="size-3.5" />
                  </button>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => addLink(colIdx)}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border/60 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
            >
              <Plus className="size-3.5" />
              Dodaj link
            </button>
          </div>
        </div>
      ))}

      {/* Merchant / point-of-sale legal info */}
      <div className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
        <h3
          className="text-base font-semibold text-foreground"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          Informacije o prodajnom mjestu
        </h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Pravni podaci agencije koji se prikazuju u footeru (obavezni za naplatu karticama). Jedan red po stavci.
        </p>

        <div className="mt-3 space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Naslov bloka
          </Label>
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-xs text-muted-foreground">BS</Label>
              <Input
                value={config.merchant?.title.bs ?? ""}
                onChange={(e) => updateMerchantTitle("bs", e.target.value)}
                placeholder="Informacije o prodajnom mjestu"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">EN</Label>
              <Input
                value={config.merchant?.title.en ?? ""}
                onChange={(e) => updateMerchantTitle("en", e.target.value)}
                placeholder="Point of sale information"
              />
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Redovi
          </Label>
          {(config.merchant?.lines ?? []).map((line, lineIdx) => (
            <div key={lineIdx} className="rounded-lg border border-border/50 bg-muted/20 p-3">
              <div className="flex items-start gap-2">
                <div className="grid min-w-0 flex-1 gap-1.5 sm:grid-cols-2">
                  <Input
                    value={line.bs}
                    onChange={(e) => updateMerchantLine(lineIdx, "bs", e.target.value)}
                    placeholder="Tekst (BS)"
                    className="text-sm"
                  />
                  <Input
                    value={line.en}
                    onChange={(e) => updateMerchantLine(lineIdx, "en", e.target.value)}
                    placeholder="Text (EN)"
                    className="text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeMerchantLine(lineIdx)}
                  className="shrink-0 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-rose-50 hover:text-rose-600"
                >
                  <Trash className="size-3.5" />
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addMerchantLine}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border/60 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
          >
            <Plus className="size-3.5" />
            Dodaj red
          </button>
        </div>
      </div>

      {/* Social media */}
      <div className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
        <h3
          className="text-base font-semibold text-foreground"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          Društvene mreže
        </h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Ostavite polje prazno da sakrijete mrežu. Prikazuju se samo popunjene.
        </p>
        <div className="mt-3 space-y-2">
          {SOCIAL_PLATFORMS.map(({ platform, label, placeholder }) => {
            const value =
              (config.socials ?? []).find((s) => s.platform === platform)?.url ?? "";
            return (
              <div key={platform}>
                <Label className="text-xs text-muted-foreground">{label}</Label>
                <Input
                  type="url"
                  value={value}
                  onChange={(e) => setSocialUrl(platform, e.target.value)}
                  placeholder={placeholder}
                  className="text-sm"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Copyright */}
      <div className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
        <h3
          className="text-base font-semibold text-foreground"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          Copyright tekst
        </h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Koristite <code className="rounded bg-muted px-1 text-xs">{"{year}"}</code> za automatsku godinu.
        </p>
        <div className="mt-3 space-y-2">
          <div>
            <Label className="text-xs text-muted-foreground">Bosanski</Label>
            <Input
              value={config.copyright.bs}
              onChange={(e) => updateCopyright("bs", e.target.value)}
              placeholder="© {year} Psihotron..."
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">English</Label>
            <Input
              value={config.copyright.en}
              onChange={(e) => updateCopyright("en", e.target.value)}
              placeholder="© {year} Psihotron..."
            />
          </div>
        </div>
      </div>

      {/* Save bar */}
      <div
        className={cn(
          "sticky bottom-4 flex items-center justify-between rounded-xl border bg-card/95 p-4 shadow-lg backdrop-blur-sm transition-all sm:p-5",
          hasChanges ? "ring-2 ring-primary/20" : "ring-1 ring-border/50"
        )}
      >
        <div className="flex items-center gap-2 text-sm">
          {hasInvalidHref ? (
            <>
              <Warning className="size-4 text-amber-500" />
              <span className="text-muted-foreground">Neispravan URL u linkovima</span>
            </>
          ) : saved ? (
            <>
              <CheckCircle className="size-4 text-emerald-600" />
              <span className="font-medium text-emerald-600">Footer spremljen</span>
            </>
          ) : hasChanges ? (
            <>
              <Warning className="size-4 text-amber-500" />
              <span className="text-muted-foreground">Imate nesačuvane promjene</span>
            </>
          ) : (
            <span className="text-muted-foreground">Footer je ažuran</span>
          )}
        </div>
        <Button
          type="submit"
          disabled={saveMutation.isPending || !hasChanges || hasInvalidHref}
        >
          {saveMutation.isPending ? (
            <CircleNotch className="size-4 animate-spin" />
          ) : (
            <FloppyDisk className="size-4" />
          )}
          {saveMutation.isPending ? "Spremanje..." : "Spremi footer"}
        </Button>
      </div>
    </form>
  );
}
